import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Components
import MuiHeader from "../../components/MuiHeader";
import MuiLoader from "../../components/MuiLoader";

// Thunks
import {
  getpgeactivityskillThunk,
  createpgeskillmasterThunk,
  updatepgeskillmasterThunk,
  deletepgeskillmasterThunk,
} from "./PgeSkillsetManager.Thunk";
import {
  preferedlanguage_change,
  class_change,
  subject_change,
} from "./PgeSkillsetManager.Slice";

// MUI
import {
  Avatar,
  Button,
  InputLabel,
  FormControl,
  Select,
  Box,
  Grid,
  Card,
  CardHeader,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AodIcon from "@mui/icons-material/Aod";
import { red, blue, green, purple } from "@mui/material/colors";

// toastify
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// swal
import Swal from "sweetalert2";

function PgeSkillsetManagerPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ------------ Grid gallery view -----------
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  // ---------------------------------------------

  // --------- options for dropdowns ---------
  const language_select_option_list = [
    { value: "od", text: "ଓଡିଆ" },
    { value: "en", text: "English" },
  ];
  const subject_select_option_list = [
    { value: "math", text: "Math" },
    { value: "english", text: "English" },
    { value: "odia", text: "Odia" },
    { value: "evs", text: "EVS" },
  ];
  const class_select_option_list = [
    { value: "1", text: "1" },
    { value: "2", text: "2" },
    { value: "3", text: "3" },
    { value: "4", text: "4" },
    { value: "5", text: "5" },
  ];
  // ------------------------------------------

  // ------------ dropdowns on change ----------------
  const preflanguage_select_onchange = (event) => {
    dispatch(preferedlanguage_change(event.target.value));
  };
  const subject_select_onchange = (event) => {
    dispatch(subject_change(event.target.value));
  };
  const class_select_onchange = (event) => {
    dispatch(class_change(event.target.value));
  };
  // -------------------------------------------------

  // -------------- Loader -----------------------
  const loading = useSelector((state) => state.PgeSkillsetManagerSlice.loading);
  // ---------------------------------------------

  // -------------- Card-item vertical menu ---------------
  const ITEM_HEIGHT = 48;
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recordId, setRecordId] = useState("");
  const [selectedSkillsetId, setSelectedSkillsetId] = useState("");
  const [selectedSkillsetName, setSelectedSkillsetName] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const openVertMenu = Boolean(anchorEl);
  const handleVertMenuClick = (event, _id, skillsetid, skillsetname, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedIndex(index);
    setRecordId(_id);
    setSelectedSkillsetId(skillsetid);
    setSelectedSkillsetName(skillsetname);
  };
  const handleVertMenuClose = () => {
    setAnchorEl(null);
  };
  // ---------------------------------------------

  // --------------------- data --------------------------
  const data = useSelector((state) => state.PgeSkillsetManagerSlice.data);
  // -----------------------------------------------------

  // ---------------- navigate to skillset manager -------------
  const navigate_to = () => {
    navigate("/pgeactivity");
  };
  // ------------------------------------------------------------

  // ------------ Load data & skill ----------------
  const program = "pge";
  const preferedlanguage = useSelector(
    (state) => state.PgeSkillsetManagerSlice.preferedlanguage
  );
  const subject = useSelector((state) => state.PgeSkillsetManagerSlice.subject);
  const clas = useSelector((state) => state.PgeSkillsetManagerSlice.clas);
  const disable_content_button = useSelector(
    (state) => state.PgeSkillsetManagerSlice.disable_content_button
  );

  useEffect(() => {
    const load_data = () => {
      if (
        preferedlanguage.length > 0 &&
        subject.length > 0 &&
        clas.length > 0
      ) {
        const params = { preferedlanguage, program, subject, clas };
        dispatch(getpgeactivityskillThunk(params));
      }
    };
    load_data();
  }, [dispatch, preferedlanguage, subject, clas]);
  // -----------------------------------------------

  // ----------- create, update delete button click ----------
  const create_content_button_click = () => {
    Swal.fire({
      title: "CREATE NEW SKILLSET",
      input: "text",
      inputAttributes: {
        autocapitalize: "off",
      },
      showCancelButton: true,
      confirmButtonText: "Save",
      showLoaderOnConfirm: true,
      preConfirm: (input_value) => {
        if (
          input_value === undefined ||
          input_value === null ||
          input_value.length <= 0
        )
          toast.error("Enter a valid skillset name");
        else {
          const body = {
            preferedlanguage,
            program,
            class: clas,
            subject,
            skillsetid: new Date().getTime(),
            skillsetname: input_value,
          };

          dispatch(createpgeskillmasterThunk(body)).then(() => {
            const params = { preferedlanguage, program, subject, clas };
            dispatch(getpgeactivityskillThunk(params));
            toast.success("Changes made successfully");
          });
        }
      },
    });
  };

  const update_content_button_click = (_id, skillsetname) => {
    handleVertMenuClose();
    Swal.fire({
      title: "UPDATE SKILLSET",
      input: "text",
      inputValue: skillsetname,
      inputAttributes: {
        autocapitalize: "off",
      },
      showCancelButton: true,
      confirmButtonText: "Update",
      showLoaderOnConfirm: true,
      preConfirm: (input_value) => {
        if (input_value === skillsetname) toast.error("No changes made");
        else if (
          input_value === undefined ||
          input_value === null ||
          input_value.length <= 0
        )
          toast.error("Enter a valid skillset name");
        else {
          const params = {
            _id: _id,
            body: {
              skillsetname: input_value,
            },
          };
          dispatch(updatepgeskillmasterThunk(params)).then(() => {
            const params = { preferedlanguage, program, subject, clas };
            dispatch(getpgeactivityskillThunk(params));
            toast.success("Changes made successfully");
          });
        }
      },
    });
  };

  const delete_content_button_click = (_id) => {
    handleVertMenuClose();
    Swal.fire({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deletepgeskillmasterThunk(_id)).then(() => {
          const params = { preferedlanguage, program, subject, clas };
          dispatch(getpgeactivityskillThunk(params));
          toast.success("Changes made successfully");
        });
      }
    });
  };
  // -------------------------------------------------

  return (
    <>
      <div>
        <MuiHeader header="PGE Skillset Manager" />

        <FormControl sx={{ m: 1, minWidth: 120, marginTop: 10 }} size="small">
          <InputLabel id="demo-select-small">Language</InputLabel>
          <Select
            labelId="demo-select-small"
            id="demo-select-small"
            value={preferedlanguage}
            label="Language"
            onChange={preflanguage_select_onchange}>
            <MenuItem value="">None</MenuItem>
            {language_select_option_list.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ m: 1, minWidth: 120, marginTop: 10 }} size="small">
          <InputLabel id="demo-select-small">Subject</InputLabel>
          <Select
            labelId="demo-select-small"
            id="demo-select-small"
            value={subject}
            label="Subject"
            onChange={subject_select_onchange}>
            <MenuItem value="">None</MenuItem>
            {subject_select_option_list.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ m: 1, minWidth: 120, marginTop: 10 }} size="small">
          <InputLabel id="demo-select-small">Class</InputLabel>
          <Select
            labelId="demo-select-small"
            id="demo-select-small"
            value={clas}
            label="Class"
            onChange={class_select_onchange}>
            <MenuItem value="">None</MenuItem>
            {class_select_option_list.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          onClick={navigate_to}
          variant="outlined"
          sx={{ m: 1, minWidth: 120 , marginTop: 0}}
          size="small">
          PGE Activities
        </Button>
      </div>

      {/* Show List */}
      <div>
        <Button
        style={{marginTop: -36}}
          onClick={create_content_button_click}
          variant="contained"
          disabled={disable_content_button}>
          Create New Skill
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={3}>
            {data.map((eachskill, index) => (
              <Grid item xs={12} sm={isSmallScreen ? 12 : 6} md={4} key={index}>
                <Card sx={{ maxWidth: 345 }}>
                  <CardHeader
                    avatar={
                      <Avatar sx={{ bgcolor: purple[500] }}>
                        <AodIcon />
                      </Avatar>
                    }
                    action={
                      <>
                        <IconButton
                          id={index + 1.1}
                          onClick={(e) =>
                            handleVertMenuClick(
                              e,
                              eachskill._id,
                              eachskill.skillsetid,
                              eachskill.skillsetname,
                              index
                            )
                          }
                          aria-controls={openVertMenu ? { index } : undefined}
                          aria-expanded={openVertMenu ? "true" : undefined}
                          aria-haspopup="true">
                          <MoreVertIcon />
                        </IconButton>
                      </>
                    }
                    title={
                      <div style={{ cursor: "pointer" }}>
                        {eachskill.skillsetname}
                      </div>
                    }
                    subheader={
                      <div style={{ cursor: "pointer" }}>
                        {eachskill.skillsetid ? eachskill.skillsetid : ""}
                      </div>
                    }
                  />
                  {/* <CardMedia component="img" height="194" image={require("../../media/images/content.webp")} alt="content" />
                  <CardContent></CardContent> */}
                </Card>
              </Grid>
            ))}
            <Menu
              id={selectedIndex}
              anchorEl={anchorEl}
              open={openVertMenu}
              onClose={handleVertMenuClose}
              MenuListProps={{
                "aria-labelledby": "vert-button",
              }}
              PaperProps={{
                style: {
                  maxHeight: ITEM_HEIGHT * 4.5,
                  width: "20ch",
                },
              }}>
              <MenuItem
                onClick={() =>
                  update_content_button_click(recordId, selectedSkillsetName)
                }>
                Edit
              </MenuItem>
              <MenuItem onClick={() => delete_content_button_click(recordId)}>
                Delete
              </MenuItem>
            </Menu>
          </Grid>
        </Box>
      </div>

      {/* Loader */}
      <MuiLoader open={loading} />

      {/* Toaster */}
      <ToastContainer />
    </>
  );
}

export default PgeSkillsetManagerPage;
